/**
 * Apphud Pixel Manager
 * Handles Apphud pixel initialization after consent
 */

import type { AnalyticsEventData, ConsentManager } from '../types/analytics.types';
import type { StatsManager } from '../core/stats-manager';
import type { Config } from '../types/config.types';
import type { EventName, EventData, EventMetadata } from '../types/events.types';
import { ConsentGatedPixel, NON_FORWARDED_EVENTS } from './consent-gated-pixel';

/**
 * Dependencies for ApphudPixelManager
 */
interface ApphudPixelDeps {
  consentManager: ConsentManager;
  statsManager: StatsManager;
  config: Config;
}

/**
 * Apphud Pixel Manager
 */
export class ApphudPixelManager extends ConsentGatedPixel<ApphudPixelDeps> {
  /**
   * Initialize Apphud Pixel Manager
   */
  init(deps: Partial<ApphudPixelDeps>): void {
    this.requireDeps(deps, ['consentManager', 'statsManager', 'config'], 'ApphudPixelManager');

    console.log('Apphud Pixel Manager initialized (waiting for consent)');

    // Initialize the vendor + subscribe once full consent is granted (immediate where not required).
    this.initOnFullConsent();
  }

  /**
   * Initialize Apphud Pixel with consent
   */
  protected initializePixel(): void {
    if (this.initialized) {
      return;
    }
    if (!this.deps.consentManager) {
      console.log('Apphud pixel - not initialized');
      return;
    }
    if (!this.deps.consentManager.hasConsent()) {
      console.log('Apphud pixel - consent not given');
      return;
    }

    // Check if Apphud pixel ID is configured
    const pixelId = this.deps.config?.analytics?.apphud?.pixelId;

    if (!pixelId || pixelId === '') {
      console.warn(
        '⚠️ Apphud Pixel ID not configured in config.js. Please add your Apphud pixel ID to CONFIG.analytics.apphud.pixelId'
      );
      return;
    }

    // Initialize Apphud with pixel ID
    if (window.aph) {
      window.aph('init', pixelId);
      this.initialized = true;
      console.log('Apphud pixel initialized with ID:', pixelId);
    } else {
      console.warn('Apphud script not loaded');
    }
  }

  /**
   * Track custom event
   */
  trackEvent(eventName: string, properties: AnalyticsEventData = {}): void {
    if (!this.initialized) {
      // Consent-gated: initialization only happens in the onConsentGranted callback.
      console.log('Apphud not initialized, skipping event:', eventName);
      return;
    }

    // Track event using Apphud
    if (window.aph) {
      window.aph('track', eventName, properties);
      console.log('Apphud event tracked:', eventName, properties);
    }
  }

  /**
   * Handle events from StatsManager
   */
  protected handleStatsManagerEvent(
    eventName: EventName,
    eventData: EventData,
    _eventMetadata: EventMetadata,
    _value: string | number | null
  ): void {
    // Do NOT auto-initialize here - wait for consent
    if (!this.initialized) {
      // Silently skip if not initialized (waiting for consent)
      return;
    }

    try {
      const data = eventData as AnalyticsEventData;

      // Map generic events to Apphud events
      switch (eventName) {
        case 'email_captured':
          this.trackEmailCaptured(data.email as string);
          break;
        case 'screen_viewed':
          this.trackScreenView(data.screen as string);
          break;
        case 'funnel_step_completed':
          this.trackFunnelStepCompleted(data.step as string, data);
          break;
        case 'practice_commitment_selected':
          this.trackPracticeCommitment(data.frequency as string);
          break;
        case 'paywall_viewed':
          this.trackPaywallViewed(data);
          break;
        case 'checkout_initiated':
          this.trackCheckoutInitiated(data);
          break;
        case 'subscription_completed':
          this.trackPurchaseCompleted(data);
          break;
        default:
          // Track other relevant events
          if (!NON_FORWARDED_EVENTS.has(eventName)) {
            this.trackEvent(eventName, data);
          }
      }
    } catch (error) {
      console.error('Apphud: Failed to handle event:', eventName, error);
    }
  }

  /**
   * Track email capture
   */
  private trackEmailCaptured(email: string): void {
    this.trackEvent('EmailCaptured', { email });

    // Also identify user
    if (window.aph && this.initialized) {
      window.aph('identify', email);
      console.log('Apphud user identified:', email);
    }
  }

  /**
   * Track screen view
   */
  private trackScreenView(screenName: string): void {
    this.trackEvent('ScreenViewed', {
      screen_name: screenName,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track funnel step completed
   */
  private trackFunnelStepCompleted(step: string, data: AnalyticsEventData = {}): void {
    this.trackEvent('FunnelStepCompleted', {
      step,
      ...data,
    });
  }

  /**
   * Track paywall viewed
   */
  private trackPaywallViewed(data: AnalyticsEventData = {}): void {
    this.trackEvent('PaywallViewed', {
      price: data.price || 0,
      currency: data.currency || '',
      ...data,
    });
  }

  /**
   * Track checkout initiated
   */
  private trackCheckoutInitiated(data: AnalyticsEventData = {}): void {
    this.trackEvent('CheckoutInitiated', {
      price: data.price || 0,
      currency: data.currency || 'USD',
      ...data,
    });
  }

  /**
   * Track purchase completed
   */
  private trackPurchaseCompleted(data: AnalyticsEventData = {}): void {
    this.trackEvent('PurchaseCompleted', {
      price: data.amount, // bus emits revenue as `amount` (dollars), not `price`
      currency: data.currency || '',
      transaction_id: data.transactionId,
      ...data,
    });
  }

  /**
   * Track practice commitment
   */
  private trackPracticeCommitment(frequency: string): void {
    this.trackEvent('PracticeCommitmentSelected', {
      frequency,
    });
  }
}

// Create singleton instance
export const apphudPixelManager = new ApphudPixelManager();
